/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/Events'
], function(Events) {

	const ThumbnailStore = Events.extend({

		/**
		 * local cache
		 * @type {Object}
		 */
		_items: null,

		/**
		 * Constructor
		 *
		 * @param {Object} [items={}] - store data
		 */
		init: function(items = {}) {
			ThumbnailStore.inherited('init', this, []);
			this._items = items;
		},

		/**
		 * Check whether a thumbnail id exists in the store
		 *
		 * @param {string} id - thumbnail id
		 *
		 * @return {boolean} TRUE if exists in store and FALSE if not
		 */
		exists: function(id) {
			return this._items[id] !== undefined;
		},

		/**
		 * Set data
		 *
		 * @param {string} id - thumbnail id
		 * @param {any} data - thumbnail data
		 *
		 * @return {ThumbnailStore} self; current instance
		 */
		set: function(id, { data, cacheId }) {
			const item = {
				data,
				cacheId
			};
			this._items[id] = item;
			this.trigger('thumbnailStore:set', { id, item });
			return this;
		},

		/**
		 * Set error on the thumbnail
		 *
		 * @param {string} id - thumbnail id
		 * @param {error} error - error to store on the object
		 */
		setError: function(id, { message }) {
			let item;
			if (this.exists(id)) {
				item = this.getLocal(id);
				item.error = {
					message
				};
			} else {
				item = {
					error: {
						message
					}
				};
				this._items[id] = item;
			}
			this.trigger('thumbnailStore:set', { id, item });
			return this;
		},

		/**
		 * Set warning on the thumbnail
		 *
		 * @param {string} id - thumbnail id
		 * @param {warning} warning - warning to store on the object
		 */
		setWarning: function(id, { message }) {
			let item;
			if (this.exists(id)) {
				item = this.getLocal(id);
				item.warning = {
					message
				};
			} else {
				item = {
					warning: {
						message
					}
				};
				this._items[id] = item;
			}
			this.trigger('thumbnailStore:set', { id, item });
			return this;
		},

		/**
		 * Get data locally, from the cache
		 *
		 * @param {string} id - thumbnail id
		 *
		 * @return {any} thumbnail data
		 */
		getLocal: function(id) {
			return this._items[id];
		},

		/**
		 * Get all data locally, from the cache
		 *
		 * @return {Object[]} array of thumbnail data
		 */
		getAllLocal: function() {
			return Object.keys(this._items)
				.map((id) => {
					const item = this._items[id];
					return {
						id,
						item
					};
				});
		},

		/**
		 * Get data
		 *
		 * @param {string} id - thumbnail id
		 * @param {string} type - vis type
		 *
		 * @return {any} thumbnail data
		 */
		get: function(id, type) {
			let result;
			if (this.exists(id)) {
				result = Promise.resolve(this.getLocal(id));
			} else {
				result = this.getVisTypeDefault(type);
			}
			return result;
		},

		/**
		 * Delete data
		 *
		 * @param {string} id - thumbnail id
		 *
		 * @return {ThumbnailStore} self; current instance
		 */
		delete: function(id) {
			delete this._items[id];
			this.trigger('thumbnailStore:delete', { id });
			return this;
		},

		/**
		 * Get default thumbnail for a specific vis type
		 *
		 * @param {string} type - vis type
		 *
		 * @return {string} default thumbnail
		 */
		getVisTypeDefault: function(/* type */) {
			return Promise.resolve();
		}
	});

	return ThumbnailStore;
});
